<template>
	<div
		class="sun-tracker-controls"
		v-if="active"
		:style="{ top: position.y + 'px', left: position.x + 'px' }"
	>
		<div class="info-panel">
			<div class="header" @mousedown="startDrag">
				<h4>☀️ Posizione Sole</h4>
				<button class="close-btn" @click="closeTracker()">✕</button>
			</div>
			<template v-if="referenceLocation">
				<div class="sun-times" v-if="sunTimes">
					<div class="time-row">
						<span>🌅 Alba:</span>
						<span>{{ formatTime(sunTimes.sunrise) }}</span>
					</div>
					<div class="time-row">
						<span>🏙️ Tramonto:</span>
						<span>{{ formatTime(sunTimes.sunset) }}</span>
					</div>
					<div class="time-row">
						<span>🕛 Mezzogiorno:</span>
						<span>{{ formatTime(sunTimes.solarNoon) }}</span>
					</div>
				</div>
				<div class="sun-pos" v-if="sunPos">
					<div>
						Azimuth: {{ Math.round((sunPos.azimuth * 180) / Math.PI + 180) }}°
					</div>
					<div>
						Altitudine: {{ Math.round((sunPos.altitude * 180) / Math.PI) }}°
					</div>
				</div>
			</template>
			<div class="hint" v-else>Clicca sulla mappa per posizionare</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import L from "leaflet";
import SunCalc from "suncalc";

const props = defineProps<{
	map: L.Map | null;
	date: Date;
	active: boolean;
}>();

const emit = defineEmits(["close"]);

const referenceLocation = ref<L.LatLng | null>(null);
const sunTimes = ref<any>(null);
const sunPos = ref<any>(null);

// Drag State
// Start in the top right corner of the map container
const position = ref({ x: 0, y: 15 });
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

function resetPosition() {
	if (!props.map) return;
	const container = props.map.getContainer();
	const width = container.clientWidth;
	const height = container.clientHeight;

	if (width < 768) {
		// Mobile: Bottom-left
		position.value = { x: 15, y: height - 220 };
	} else {
		// Desktop: Top-left (offset to be below icons)
		position.value = { x: 20, y: 75 };
	}
}

onMounted(() => {
	resetPosition();
});

// Leaflet Layers
const layerGroup = L.layerGroup();
const RADIUS_METERS = 500; // Radius of the visual circle

watch(
	() => props.active,
	(newVal) => {
		if (newVal) {
			resetPosition();
			if (props.map) {
				props.map.on("click", onMapClick);
				layerGroup.addTo(props.map);
			}
		} else {
			// When deactivating, fully reset the state
			closeTracker(false);
		}
	},
);

watch(
	() => props.date,
	() => {
		if (props.active && referenceLocation.value) {
			updateVisuals();
		}
	},
);

function closeTracker(emitClose = true) {
	console.log("SunTracker: closing and resetting state");
	if (props.map) {
		props.map.off("click", onMapClick);
		layerGroup.clearLayers();
		layerGroup.remove();
	}
	// Fully reset state to show hint next time
	referenceLocation.value = null;
	sunTimes.value = null;
	sunPos.value = null;

	if (emitClose) emit("close");
}

function onMapClick(e: L.LeafletMouseEvent) {
	referenceLocation.value = e.latlng;
	updateVisuals();
}

// Drag handlers
function startDrag(e: MouseEvent) {
	if ((e.target as HTMLElement).classList.contains("close-btn")) return;

	isDragging.value = true;
	dragOffset.value = {
		x: e.clientX - position.value.x,
		y: e.clientY - position.value.y,
	};

	document.addEventListener("mousemove", onDrag);
	document.addEventListener("mouseup", stopDrag);
}

function onDrag(e: MouseEvent) {
	if (!isDragging.value) return;

	position.value = {
		x: e.clientX - dragOffset.value.x,
		y: e.clientY - dragOffset.value.y,
	};
}

function stopDrag() {
	isDragging.value = false;
	document.removeEventListener("mousemove", onDrag);
	document.removeEventListener("mouseup", stopDrag);
}

function updateVisuals() {
	if (!referenceLocation.value || !props.map) return;

	layerGroup.clearLayers();
	const center = referenceLocation.value;
	const date = props.date || new Date();

	// 1. Calculate Times
	const times = SunCalc.getTimes(date, center.lat, center.lng);
	sunTimes.value = times;

	// 2. Format Labels
	// Draw Horizon Circle
	L.circle(center, {
		radius: RADIUS_METERS,
		color: "#666",
		fill: false,
		weight: 1,
		dashArray: "5, 5",
	}).addTo(layerGroup);

	// 3. Draw Sun Path
	// Sample every 15 mins from sunrise to sunset
	const pathPoints: L.LatLng[] = [];
	let curr = new Date(times.sunrise.getTime() - 30 * 60000); // Start bit before
	const end = new Date(times.sunset.getTime() + 30 * 60000); // End bit after

	while (curr <= end) {
		const pos = SunCalc.getPosition(curr, center.lat, center.lng);
		const point = projectPosition(center, pos.azimuth, pos.altitude);
		if (point) pathPoints.push(point);
		curr = new Date(curr.getTime() + 15 * 60000);
	}

	if (pathPoints.length > 0) {
		L.polyline(pathPoints, { color: "orange", weight: 3 }).addTo(layerGroup);
	}

	// 4. Current Sun Position
	const currentPos = SunCalc.getPosition(date, center.lat, center.lng);
	sunPos.value = currentPos;
	const sunLatLng = projectPosition(
		center,
		currentPos.azimuth,
		currentPos.altitude,
	);

	if (sunLatLng) {
		const sunIcon = L.divIcon({
			className: "sun-icon-marker",
			html: '<div style="font-size: 24px;">☀️</div>',
			iconSize: [30, 30],
			iconAnchor: [15, 15],
		});
		L.marker(sunLatLng, { icon: sunIcon }).addTo(layerGroup);

		// Line from center to sun
		L.polyline([center, sunLatLng], {
			color: "orange",
			weight: 1,
			dashArray: "2,4",
		}).addTo(layerGroup);
	}

	// 5. Sunrise/Sunset Lines
	const sunrisePos = SunCalc.getPosition(times.sunrise, center.lat, center.lng);
	const sunsetPos = SunCalc.getPosition(times.sunset, center.lat, center.lng);

	const sunrisePt = projectPosition(center, sunrisePos.azimuth, 0); // Horizon
	const sunsetPt = projectPosition(center, sunsetPos.azimuth, 0); // Horizon

	if (sunrisePt)
		L.polyline([center, sunrisePt], { color: "blue", weight: 1 }).addTo(
			layerGroup,
		);
	if (sunsetPt)
		L.polyline([center, sunsetPt], { color: "red", weight: 1 }).addTo(
			layerGroup,
		);
}

// Helper: Project Azimuth/Altitude to Map Coordinate offset from Center
function projectPosition(
	center: L.LatLng,
	azimuth: number,
	altitude: number,
): L.LatLng | null {
	// Azimuth: 0 is South, Math.PI * 3/4 is Northwest... SunCalc convention:
	// 0 = South, increases clockwise?? No.
	// SunCalc: azimuth is radians. 0 is South, Math.PI/2 is West. -Math.PI/2 is East.
	// We want to project this onto a circle on the map.
	// Map Angle (Leaflet/North-up): 0 deg (Up) is North.
	// SunCalc 0 (South) -> Map 180 (Down).
	// SunCalc PI/2 (West) -> Map 270 (Left).
	// So MapAngle = Azimuth + Math.PI.

	const mapAngle = azimuth + Math.PI;

	// Distance from center:
	// If we want a "Solar Chart" (Zenith at center), dist = R * (1 - alt / (PI/2))

	let r = RADIUS_METERS * (1 - altitude / (Math.PI / 2));
	// Clamp min r ??

	const R_EARTH = 6378137;
	const dLat = ((r * Math.cos(mapAngle)) / R_EARTH) * (180 / Math.PI);
	const dLng =
		((r * Math.sin(mapAngle)) /
			(R_EARTH * Math.cos((center.lat * Math.PI) / 180))) *
		(180 / Math.PI);

	return L.latLng(center.lat + dLat, center.lng + dLng);
}

function formatTime(d: Date) {
	return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Public Method for Parent
function setLocation(latlng: L.LatLng) {
	referenceLocation.value = latlng;
	updateVisuals();
}

defineExpose({ setLocation });

onBeforeUnmount(() => {
	closeTracker(false);
});
</script>

<style scoped>
.sun-tracker-controls {
	position: absolute;
	z-index: 2000;
}

.info-panel {
	background: rgba(20, 24, 28, 0.95);
	backdrop-filter: blur(10px);
	border: 1px solid rgba(255, 255, 255, 0.1);
	color: white;
	padding: 12px;
	border-radius: 8px;
	width: 200px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	padding-bottom: 4px;
	margin-bottom: 8px;
	cursor: move;
	user-select: none;
}
.header h4 {
	margin: 0;
	font-size: 0.9rem;
	font-weight: 600;
}

.close-btn {
	background: none;
	border: none;
	color: #999;
	cursor: pointer;
}
.close-btn:hover {
	color: white;
}

.time-row {
	display: flex;
	justify-content: space-between;
	font-size: 0.85rem;
	margin-bottom: 4px;
}

.sun-pos {
	margin-top: 8px;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
	padding-top: 4px;
	font-size: 0.8rem;
	color: #ffd54f;
}

.hint {
	font-style: italic;
	color: #888;
	font-size: 0.8rem;
	text-align: center;
}
</style>
